import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import V10PredictionChart from './V10PredictionChart';
import TuneScorecard from './TuneScorecard';
import DeriveTuneDialog from './DeriveTuneDialog';
import { useAuth } from '../contexts/AuthContext';
import {
  History, GitCompare, Download, ChevronDown, ChevronUp, ArrowRight, Loader2,
  Shield, Flame, Zap, Pin, PinOff, Sparkles, Stethoscope,
  CheckCircle2, AlertTriangle, X, Cpu, Gauge, FileCheck2, Wrench,
  Eye, EyeOff, Star, Upload as UploadIcon, Wand2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Per-version build storyboard. Each step shows for ~1.4s; the actual API call
// runs in parallel. If the API completes faster, we fast-forward; if slower, we
// hold on the last step until the response lands.
const BUILD_STORYBOARDS = {
  V10: {
    title: 'V10 TopEndFix',
    color: 'from-[#FF5E1A] to-amber-500',
    accent: '#FF5E1A',
    tagline: 'Surgical +30% top-end enrichment on V9 base',
    steps: [
      { icon: Cpu,        label: 'Loading V9 base bin from GridFS' },
      { icon: Sparkles,   label: 'Applying +30% on rows 14-15 (6500-7000 RPM)' },
      { icon: Sparkles,   label: '+10% maintenance on rows 10-13 (5500-6400 RPM)' },
      { icon: Sparkles,   label: '+3% main fuel · ceiling 0xFA00 → 0xFE00' },
      { icon: Shield,     label: 'Verifying FORBIDDEN regions untouched' },
      { icon: FileCheck2, label: 'Persisting to GridFS · sha256 + dual-write' },
    ],
  },
  V11: {
    title: 'V11 BestOfBoth',
    color: 'from-emerald-500 to-cyan-500',
    accent: '#10B981',
    tagline: 'V9 broadband preserved · V10 top-end delta grafted on',
    steps: [
      { icon: Cpu,        label: 'Loading V9 base bin from GridFS' },
      { icon: Sparkles,   label: 'Applying +30% on rows 14-15 ONLY (top-end unlock)' },
      { icon: Shield,     label: 'Skipping main-fuel + ceiling changes (V9 preserved)' },
      { icon: Shield,     label: 'Verifying FORBIDDEN regions untouched' },
      { icon: FileCheck2, label: 'Persisting to GridFS · sha256 + dual-write' },
    ],
  },
  V12: {
    title: 'V12 E85 SAFE',
    color: 'from-red-500 to-rose-500',
    accent: '#EF4444',
    tagline: 'Bridge tune for E85 in tank · sacrifices peak HP for detonation margin',
    steps: [
      { icon: Cpu,        label: 'Loading V11 base bin from GridFS' },
      { icon: Sparkles,   label: '+20% main fuel high-load cells (200 cells)' },
      { icon: Sparkles,   label: '+25% WOT lambda enrichment rows 4-15 (191 cells)' },
      { icon: Gauge,      label: '−3° timing pull on WOT cells (110 cells)' },
      { icon: Shield,     label: 'Re-asserting factory 7000 RPM rev limit' },
      { icon: Shield,     label: 'Verifying FORBIDDEN regions untouched' },
      { icon: FileCheck2, label: 'Persisting to GridFS · sha256 + dual-write' },
    ],
  },
  V13: {
    title: 'V13 Alpha-N E85',
    color: 'from-purple-500 to-fuchsia-500',
    accent: '#A855F7',
    tagline: 'NO-MAF survival tune · defensive over-fueling for E85 with MAF disconnected',
    steps: [
      { icon: Cpu,        label: 'Loading V12 SAFE base bin from GridFS' },
      { icon: Sparkles,   label: '+30% main fuel high-load cells (catches MAF-fault path)' },
      { icon: Sparkles,   label: '+25% load fuel map (redundant fueling for SD backup)' },
      { icon: Sparkles,   label: '+15% lambda enrichment rows 4-15 (target AFR 9.5-10.8)' },
      { icon: Gauge,      label: '−3° additional WOT timing (−6° total vs V11)' },
      { icon: Shield,     label: 'Re-asserting factory 7000 RPM rev limit' },
      { icon: Shield,     label: 'Verifying FORBIDDEN regions untouched' },
      { icon: FileCheck2, label: 'Persisting to GridFS · sha256 + dual-write' },
    ],
  },
};

function GenerationOverlay({ state, onClose }) {
  // state shape: {version, status: 'building'|'success'|'error', stepIdx, result, error}
  if (!state) return null;
  const sb = BUILD_STORYBOARDS[state.version];
  if (!sb) return null;
  const { status, stepIdx, result, error } = state;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="generation-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-xl bg-[#0F0F12] border border-[#27272A] rounded-sm shadow-[0_0_80px_rgba(255,94,26,0.25)]">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-[#27272A] bg-gradient-to-r ${sb.color}/10 flex items-start gap-3`}>
          <div
            className={`w-10 h-10 flex-shrink-0 rounded-sm flex items-center justify-center bg-gradient-to-br ${sb.color}`}
            style={{ boxShadow: `0 0 25px ${sb.accent}55` }}
          >
            {status === 'success'
              ? <CheckCircle2 className="w-5 h-5 text-white" />
              : status === 'error'
                ? <AlertTriangle className="w-5 h-5 text-white" />
                : <Loader2 className="w-5 h-5 text-white animate-spin" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-400 mb-0.5">
              {status === 'success' ? 'Tune ready · click download' : status === 'error' ? 'Generation failed' : 'AI is building your tune…'}
            </div>
            <div className="font-['Outfit'] text-lg font-bold text-white">{sb.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{sb.tagline}</div>
          </div>
          {(status === 'success' || status === 'error') && (
            <button
              onClick={onClose}
              data-testid="close-generation-overlay"
              className="p-1 text-zinc-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {status !== 'error' && (
            <ul className="space-y-2.5" data-testid="generation-steps">
              {sb.steps.map((s, i) => {
                const done = status === 'success' || stepIdx > i;
                const active = status === 'building' && stepIdx === i;
                const StepIcon = s.icon;
                return (
                  <li
                    key={s.label}
                    data-testid={`gen-step-${i}`}
                    className={`flex items-center gap-3 text-sm transition-all ${
                      done ? 'text-emerald-400' : active ? 'text-white' : 'text-zinc-600'
                    }`}
                  >
                    <span
                      className={`w-7 h-7 flex-shrink-0 rounded-sm flex items-center justify-center border ${
                        done
                          ? 'bg-emerald-500/15 border-emerald-500/40'
                          : active
                            ? 'bg-[#FF5E1A]/15 border-[#FF5E1A]/40'
                            : 'bg-[#18181B] border-[#27272A]'
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : active ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <StepIcon className="w-3.5 h-3.5" />
                      )}
                    </span>
                    <span className={`font-['JetBrains_Mono'] text-xs ${active ? 'tracking-wide' : ''}`}>{s.label}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Success summary */}
          {status === 'success' && result && (
            <div className="mt-5 p-4 bg-emerald-500/5 border border-emerald-500/30 rounded-sm space-y-3" data-testid="generation-success-summary">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-zinc-500 font-['JetBrains_Mono'] uppercase text-[9px] tracking-wider mb-0.5">Bytes changed</div>
                  <div className="text-white font-bold font-['JetBrains_Mono']">{result.total_bytes_changed_vs_base ?? '—'}</div>
                </div>
                <div>
                  <div className="text-zinc-500 font-['JetBrains_Mono'] uppercase text-[9px] tracking-wider mb-0.5">Safety check</div>
                  <div className={`font-bold font-['JetBrains_Mono'] ${result.safety_ok ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.safety_ok ? '✓ PASS' : '✗ FAIL'}
                  </div>
                </div>
                {result.predicted_outcomes?.peak_hp_crank && (
                  <div className="col-span-2">
                    <div className="text-zinc-500 font-['JetBrains_Mono'] uppercase text-[9px] tracking-wider mb-0.5">Predicted peak HP</div>
                    <div className="text-white text-sm">{result.predicted_outcomes.peak_hp_crank}</div>
                  </div>
                )}
                {result.predicted_outcomes?.afr_wot_target && (
                  <div className="col-span-2">
                    <div className="text-zinc-500 font-['JetBrains_Mono'] uppercase text-[9px] tracking-wider mb-0.5">WOT AFR target</div>
                    <div className="text-white text-sm">{result.predicted_outcomes.afr_wot_target}</div>
                  </div>
                )}
                {result.sha256 && (
                  <div className="col-span-2">
                    <div className="text-zinc-500 font-['JetBrains_Mono'] uppercase text-[9px] tracking-wider mb-0.5">SHA-256</div>
                    <div className="text-zinc-400 font-['JetBrains_Mono'] text-[10px] break-all">{result.sha256}</div>
                  </div>
                )}
              </div>
              {result.clean_download_url && (
                <a
                  href={`${API_URL}${result.clean_download_url}`}
                  download={result.clean_filename}
                  data-testid="generation-download-btn"
                  className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-sm font-bold text-xs tracking-wider uppercase text-white transition-all bg-gradient-to-r ${sb.color} hover:brightness-110`}
                >
                  <Download className="w-4 h-4" /> Download {sb.title}.bin
                </a>
              )}
              <div className="text-[10px] text-zinc-500 font-['JetBrains_Mono'] text-center">
                Also available below in your tune timeline
              </div>
            </div>
          )}

          {/* Error state */}
          {status === 'error' && (
            <div className="p-4 bg-red-500/10 border border-red-500/40 rounded-sm" data-testid="generation-error-summary">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-white font-bold">Couldn't build your tune</div>
              </div>
              <div className="text-xs text-zinc-300 mb-3 font-['JetBrains_Mono']">{error || 'Unknown error'}</div>
              <div className="text-[10px] text-zinc-500 leading-relaxed">
                {error?.includes('401') || error?.includes('auth')
                  ? 'You may need to sign in again. Refresh the page and retry.'
                  : 'Try again in a moment, or contact support@aitunerproportal.com if it persists.'}
              </div>
            </div>
          )}

          {/* Building footer hint */}
          {status === 'building' && (
            <div className="mt-5 text-center text-[10px] text-zinc-500 font-['JetBrains_Mono']">
              Typical build time: 3–8 seconds · do not close this window
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScalingBadge({ label, value }) {
  // Defend against missing values — derived tunes use a different scaling
  // shape (combined_fuel_scale) and don't populate idle/low/mid/high/load.
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const color = value > 1.3 ? 'text-red-400 bg-red-400/10' :
    value > 1.1 ? 'text-amber-400 bg-amber-400/10' :
    value > 1.0 ? 'text-emerald-400 bg-emerald-400/10' :
    'text-zinc-400 bg-zinc-800';
  return (
    <span className={`inline-flex items-center text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-sm ${color}`}>
      {label}: {value === 1.0 ? 'stock' : `${value.toFixed(2)}x`}
    </span>
  );
}

function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <div className="space-y-2 mt-3 p-3 bg-[#050505] border border-[#27272A] rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <GitCompare className="w-3.5 h-3.5 text-cyan-400" />
        <span className="text-xs text-white font-bold">{diff.total_byte_diff} bytes different</span>
      </div>
      {Object.entries(diff.map_diffs).map(([mapId, mapDiff]) => (
        <div key={mapId} className="text-xs">
          <span className="text-zinc-400">{mapDiff.name}:</span>
          <span className="text-[#FF5E1A] font-['JetBrains_Mono'] ml-2">{mapDiff.total_changes} cells changed</span>
          {mapDiff.changes.slice(0, 5).map((c, i) => (
            <div key={i} className="flex gap-2 ml-4 text-[10px] text-zinc-500 font-['JetBrains_Mono']">
              <span>R{c.row}C{c.col}:</span>
              <span className="text-red-400">{c.file1_val}</span>
              <ArrowRight className="w-3 h-3" />
              <span className="text-emerald-400">{c.file2_val}</span>
              {c.ratio && <span className="text-zinc-600">({c.ratio}x)</span>}
            </div>
          ))}
          {mapDiff.total_changes > 5 && (
            <p className="text-[10px] text-zinc-600 ml-4">...and {mapDiff.total_changes - 5} more</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TuneTimeline() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [diffData, setDiffData] = useState({});
  const [diffLoading, setDiffLoading] = useState(null);
  const [pinning, setPinning] = useState(null);
  const [v10Generating, setV10Generating] = useState(false);
  const [v11Generating, setV11Generating] = useState(false);
  const [v12Generating, setV12Generating] = useState(false);
  const [v13Generating, setV13Generating] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [genState, setGenState] = useState(null);
  const [deriveOpen, setDeriveOpen] = useState(false);
  const [derivePrefill, setDerivePrefill] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [autoSuggesting, setAutoSuggesting] = useState(false);
  const [autoMsg, setAutoMsg] = useState(null);
  const fileInputRef = useRef(null);
  const stepTimerRef = useRef(null);

  const getAuthHeaders = useCallback(() => {
    const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Unified generator: drives the staged-progress overlay AND the API call in parallel.
  // - `version` is the storyboard key ('V10' | 'V11' | 'V12')
  // - `endpoint` is the API path ('/api/tunes/generate/v10' etc)
  // - `setGenerating` is the per-button setter so the inline spinner still works
  const runGenerator = useCallback(async (version, endpoint, setGenerating) => {
    const sb = BUILD_STORYBOARDS[version];
    if (!sb) return;

    setGenerating(true);
    setGenState({ version, status: 'building', stepIdx: 0, result: null, error: null });

    // Drive the storyboard forward at ~1.2s per step. We stop the loop when
    // the API resolves, but it keeps the UI lively while the request is in flight.
    const stepDurationMs = 1200;
    const advanceStep = () => {
      setGenState((cur) => {
        if (!cur || cur.status !== 'building') return cur;
        const next = Math.min(cur.stepIdx + 1, sb.steps.length - 1);
        return { ...cur, stepIdx: next };
      });
      stepTimerRef.current = setTimeout(advanceStep, stepDurationMs);
    };
    stepTimerRef.current = setTimeout(advanceStep, stepDurationMs);

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);

      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = `${res.status}: ${body.detail}`;
        } catch { /* keep default */ }
        setGenState({ version, status: 'error', stepIdx: sb.steps.length, result: null, error: detail });
        setGenerating(false);
        return;
      }

      const result = await res.json();
      setGenState({ version, status: 'success', stepIdx: sb.steps.length, result, error: null });
      setRefreshTick(t => t + 1);
    } catch (err) {
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
      console.error(`[TuneTimeline] ${version} generate failed:`, err);
      setGenState({ version, status: 'error', stepIdx: sb.steps.length, result: null, error: err.message || String(err) });
    } finally {
      setGenerating(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => () => {
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
  }, []);

  // Listen for the cross-component "open-derive-from-analysis" event so the
  // Generate Revision Tune button in DatalogAnalysisView can hand us a
  // pre-filled prefill payload. App.js handles the tab switch separately.
  useEffect(() => {
    const onOpen = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      setDerivePrefill({
        base_filename: detail.base_filename,
        mods: detail.mods || [],
      });
      setDeriveOpen(true);
    };
    window.addEventListener('open-derive-from-analysis', onOpen);
    return () => window.removeEventListener('open-derive-from-analysis', onOpen);
  }, []);

  useEffect(() => {
    const headers = getAuthHeaders();
    fetch(`${API_URL}/api/tune-versions`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setVersions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeaders, refreshTick]);

  const pinTune = useCallback(async (filename) => {
    setPinning(filename);
    try {
      const res = await fetch(`${API_URL}/api/tune-versions/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ filename }),
      });
      if (res.ok) setRefreshTick(t => t + 1);
    } catch (e) { console.error('[TuneTimeline] pin failed:', e); }
    setPinning(null);
  }, [getAuthHeaders]);

  const unpinTune = useCallback(async () => {
    setPinning('unpin');
    try {
      const res = await fetch(`${API_URL}/api/tune-versions/pin`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) setRefreshTick(t => t + 1);
    } catch (e) { console.error('[TuneTimeline] unpin failed:', e); }
    setPinning(null);
  }, [getAuthHeaders]);

  // Admin-only: toggle hidden / is_example flags on a tune. Backend writes
  // to db.tune_visibility (separate collection, doesn't mutate the tune doc).
  const toggleTuneVisibility = useCallback(async (filename, patch) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/tune-versions/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ filename, ...patch }),
      });
      if (res.ok) setRefreshTick(t => t + 1);
    } catch (e) { console.error('[TuneTimeline] visibility toggle failed:', e); }
  }, [getAuthHeaders]);

  // Manual .bin upload — bypasses the 3-file health check. Used when the
  // user has hand-edited a tune (WinOLS, etc.) and just wants it tracked.
  const handleUploadFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    const label = window.prompt(
      'Optional label for this revision (e.g. "V11_Hand_Smoothed_5500"):',
      file.name.replace(/\.bin$/i, '').slice(0, 40),
    );
    if (label === null) {  // user cancelled the prompt
      setUploading(false);
      e.target.value = '';
      return;
    }
    const notes = window.prompt('Optional notes (what did you change?):', '') || '';
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label || '');
      fd.append('notes', notes);
      const res = await fetch(`${API_URL}/api/tunes/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUploadError(body?.detail || `HTTP ${res.status}`);
      } else {
        setRefreshTick(t => t + 1);
      }
    } catch (err) {
      setUploadError(err.message || String(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [getAuthHeaders]);

  // One-click "Generate from Latest AI Insight". Pulls the user's most recent
  // datalog with a detected tune_version + measurable AFR, computes the AFR
  // correction, and opens the Derive dialog pre-filled. No navigation required.
  const generateFromLatestInsight = useCallback(async () => {
    setAutoSuggesting(true);
    setAutoMsg(null);
    try {
      // 1. Get the user's first vehicle.
      const vehiclesRes = await fetch(`${API_URL}/api/vehicles`, { headers: getAuthHeaders() });
      if (!vehiclesRes.ok) throw new Error('Could not load vehicles');
      const vehicles = await vehiclesRes.json();
      if (!vehicles?.length) {
        setAutoMsg('No vehicle found — upload a stock bin first.');
        return;
      }

      // 2. Get datalogs (already sorted desc by upload_date).
      const dlRes = await fetch(`${API_URL}/api/datalogs/${vehicles[0].id}`, { headers: getAuthHeaders() });
      if (!dlRes.ok) throw new Error('Could not load datalogs');
      const datalogs = await dlRes.json();

      // 3. Pick the most recent datalog that has both a detected tune_version
      //    AND a measurable AFR. Skip hidden/archived ones.
      const TARGET_AFR_BY_VERSION = {
        V9: 11.5, V10: 11.5, V11: 11.5, V11_BestOfBoth: 11.5,
        V12_SAFE: 11.0, V13_ALPHAN: 10.5,
      };
      const VERSION_TO_FILENAME = {
        V9: 'BMW_E90_328xi_E85_V9.bin',
        V10: 'BMW_E90_328xi_E85_V10.bin',
        V11: 'BMW_E90_328xi_E85_V11.bin',
        V11_BestOfBoth: 'BMW_E90_328xi_E85_V11.bin',
        V12_SAFE: 'BMW_E90_328xi_E85_V12_SAFE.bin',
        V13_ALPHAN: 'BMW_E90_328xi_E85_V13_AlphaN.bin',
      };
      const eligible = datalogs.find((d) => {
        if (d.hidden) return false;
        if (!d.tune_version || !VERSION_TO_FILENAME[d.tune_version]) return false;
        const a = d.analysis || {};
        const wot = a.wot || {};
        const wotAfrs = Object.values(wot).map((b) => b?.afr).filter((v) => typeof v === 'number' && v > 0);
        const wotAvg = wotAfrs.length ? wotAfrs.reduce((s, v) => s + v, 0) / wotAfrs.length : null;
        const overall = d.raw_stats?.afr_b1?.avg ?? a.avg_afr;
        return (typeof wotAvg === 'number' && wotAvg > 0) || (typeof overall === 'number' && overall > 0);
      });

      if (!eligible) {
        setAutoMsg('No recent datalog with a detected tune version + AFR data. Upload a labeled datalog first (filename containing V11/V10/etc.).');
        return;
      }

      const wot = eligible.analysis?.wot || {};
      const wotAfrs = Object.values(wot).map((b) => b?.afr).filter((v) => typeof v === 'number' && v > 0);
      const wotAvg = wotAfrs.length ? wotAfrs.reduce((s, v) => s + v, 0) / wotAfrs.length : null;
      const observedAfr = wotAvg ?? eligible.raw_stats?.afr_b1?.avg ?? eligible.analysis?.avg_afr;
      const targetAfr = TARGET_AFR_BY_VERSION[eligible.tune_version] || 11.5;

      // Skip if AFR is already on target.
      if (Math.abs(observedAfr - targetAfr) / targetAfr < 0.01) {
        setAutoMsg(`Latest datalog (${eligible.filename}) is already on target — no revision needed.`);
        return;
      }

      setDerivePrefill({
        base_filename: VERSION_TO_FILENAME[eligible.tune_version],
        mods: [{ id: 'afr_correction', params: { target_afr: targetAfr, actual_afr: observedAfr } }],
      });
      setDeriveOpen(true);
    } catch (err) {
      setAutoMsg(err.message || String(err));
    } finally {
      setAutoSuggesting(false);
    }
  }, [getAuthHeaders]);

  const generateV10 = useCallback(
    () => runGenerator('V10', '/api/tunes/generate/v10', setV10Generating),
    [runGenerator]
  );
  const generateV11 = useCallback(
    () => runGenerator('V11', '/api/tunes/generate/v11', setV11Generating),
    [runGenerator]
  );
  const generateV12Safe = useCallback(
    () => runGenerator('V12', '/api/tunes/generate/v12-safe', setV12Generating),
    [runGenerator]
  );
  const generateV13AlphaN = useCallback(
    () => runGenerator('V13', '/api/tunes/generate/v13-alphan', setV13Generating),
    [runGenerator]
  );

  const loadDiff = useCallback(async (file1, file2) => {
    const key = `${file1}__${file2}`;
    if (diffData[key]) return;
    setDiffLoading(key);
    try {
      const res = await fetch(`${API_URL}/api/tune-diff/${file1}/${file2}`);
      if (res.ok) {
        const data = await res.json();
        setDiffData(prev => ({ ...prev, [key]: data }));
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Diff error:', err);
    }
    setDiffLoading(null);
  }, [diffData]);

  if (loading) {
    return (
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardContent className="flex items-center justify-center gap-3 py-10">
          <Loader2 className="w-5 h-5 text-[#FF5E1A] animate-spin" />
          <span className="text-sm text-zinc-400">Loading tune history...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <GenerationOverlay state={genState} onClose={() => setGenState(null)} />
      <DeriveTuneDialog
        open={deriveOpen}
        onClose={() => { setDeriveOpen(false); setDerivePrefill(null); }}
        versions={versions}
        prefill={derivePrefill}
        onDone={() => setRefreshTick(t => t + 1)}
      />
      <Card className="bg-[#111111] border-[#27272A] rounded-sm" data-testid="tune-timeline">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
          <History className="w-5 h-5 text-[#FF5E1A]" />Tune Version Timeline
          <Badge className="bg-zinc-800 text-zinc-400 text-xs ml-2">{versions.length} versions</Badge>
          <Button
            size="sm"
            onClick={() => window.openHealthCheck?.()}
            data-testid="run-health-check-btn"
            className="ml-auto bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(6,182,212,0.35)]"
            title="3-file health check: stock bin + idle log + 3rd gear pull"
          >
            <Stethoscope className="w-3 h-3 mr-1" />Health Check
          </Button>
          <Button
            size="sm"
            onClick={generateFromLatestInsight}
            disabled={autoSuggesting}
            data-testid="generate-from-latest-insight-btn"
            className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(168,85,247,0.4)]"
            title="One-click: pull your most recent datalog and open the Derive dialog pre-filled with an AFR correction"
          >
            {autoSuggesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
            {autoSuggesting ? 'Reading datalog…' : 'Generate Tune'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bin"
            onChange={handleUploadFile}
            style={{ display: 'none' }}
            data-testid="upload-tune-input"
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-tune-btn"
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(16,185,129,0.35)]"
            title="Upload a hand-modified .bin file to your timeline (bypasses health check)"
          >
            {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UploadIcon className="w-3 h-3 mr-1" />}
            {uploading ? 'Uploading…' : 'Upload Tune'}
          </Button>
          <Button
            size="sm"
            onClick={() => setDeriveOpen(true)}
            data-testid="open-derive-tune-btn"
            className="bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(255,94,26,0.35)]"
            title="Derive a new tune from any existing tune — re-scale fuel maps for new injectors / fuel pressure / etc."
          >
            <Wrench className="w-3 h-3 mr-1" />Derive from Existing
          </Button>
          <Button
            size="sm"
            onClick={generateV13AlphaN}
            disabled={v13Generating}
            data-testid="generate-v13-alphan-btn"
            className="bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-400 hover:to-fuchsia-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(168,85,247,0.4)]"
            title="V13 Alpha-N E85 — defensive NO-MAF survival tune (MAF disconnected)"
          >
            {v13Generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {v13Generating ? 'Building V13 Alpha-N...' : 'V13 Alpha-N'}
          </Button>
          <Button
            size="sm"
            onClick={generateV12Safe}
            disabled={v12Generating}
            data-testid="generate-v12-safe-btn"
            className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-400 hover:to-rose-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(239,68,68,0.4)]"
            title="V12 SAFE — bridge tune for E85 in tank, sacrifices peak power for detonation margin"
          >
            {v12Generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {v12Generating ? 'Building V12 SAFE...' : 'V12 E85 SAFE'}
          </Button>
          <Button
            size="sm"
            onClick={generateV11}
            disabled={v11Generating}
            data-testid="generate-v11-btn"
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(16,185,129,0.35)]"
          >
            {v11Generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {v11Generating ? 'Building V11...' : 'Generate V11 BestOfBoth'}
          </Button>
          <Button
            size="sm"
            onClick={generateV10}
            disabled={v10Generating}
            data-testid="generate-v10-btn"
            className="bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white text-[10px] h-7 px-3 shadow-[0_0_12px_rgba(255,94,26,0.35)]"
          >
            {v10Generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {v10Generating ? 'Building V10...' : 'V10 TopEndFix'}
          </Button>
        </CardTitle>
        <CardDescription className="text-zinc-500 text-xs">
          Every tune revision tracked with scaling parameters and diff capability. Pin one as <span className="text-emerald-400">PRODUCTION</span> to mark your canonical flash.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Checksum heads-up — counts how many tunes in the current view
            need external checksum repair, and links to the recommended tools. */}
        {(() => {
          const need = versions.filter(v => v.needs_external_checksum_fix).length;
          if (need === 0) return null;
          return (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-sm" data-testid="checksum-help-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-zinc-300 leading-relaxed">
                  <p className="font-bold text-red-300 mb-1">
                    {need} tune{need > 1 ? 's' : ''} need an external checksum repair before flashing
                  </p>
                  <p className="text-zinc-400">
                    Any tune that edits the cal block (0x040000–0x053DFF) invalidates the MSV80 cal CRC.
                    The cloud generator can't safely recompute Bosch's exact algorithm — flashing a
                    bad-checksum bin throws a checksum error and (worst case) sends the ECU to limp mode.
                    Run the .bin through any of these before flashing:
                  </p>
                  <ul className="list-disc list-inside mt-1 text-zinc-400 space-y-0.5">
                    <li><span className="text-white">WinOLS</span> — Bosch ME/MED/MSV checksum module (auto-stamps on save)</li>
                    <li><span className="text-white">MHD Flasher</span> — auto-corrects on upload to ECU</li>
                    <li><span className="text-white">BimmerLabs / FerrariTool / MS43Tool</span> — flasher with built-in checksum repair</li>
                  </ul>
                  <p className="mt-1 text-zinc-500 text-[10px]">
                    Cal-block-untouched tunes (V11 BestOfBoth, V14) flash as-is. The badge on each row tells you which is which.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
        {(uploadError || autoMsg) && (
          <div className="mb-4 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-sm text-[11px] text-amber-200 flex items-start justify-between gap-2" data-testid="tune-status-banner">
            <span>{uploadError || autoMsg}</span>
            <button
              onClick={() => { setUploadError(null); setAutoMsg(null); }}
              className="text-amber-400 hover:text-amber-200 flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[17px] top-0 bottom-0 w-px bg-[#27272A]" />

          <div className="space-y-1">
            {versions.map((v, i) => {
              const isExpanded = expanded === i;
              const prevVersion = i < versions.length - 1 ? versions[i + 1] : null;
              const diffKey = prevVersion ? `${prevVersion.filename}__${v.filename}` : null;

              return (
                <div key={v.versioned_filename || v._id || `${v.filename}-${i}`} className={`relative pl-10 ${v.hidden ? 'opacity-40' : ''}`} data-testid={`timeline-entry-${i}`}>
                  {/* Timeline dot */}
                  <div className={`absolute left-2.5 top-3 w-3 h-3 rounded-full border-2 ${
                    v.is_production ? 'bg-emerald-500 border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.7)] animate-pulse' :
                    v.is_official ? 'bg-[#FF5E1A] border-[#FF5E1A]' : 'bg-zinc-700 border-zinc-500'
                  }`} />

                  <div
                    className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                      v.is_production ? 'bg-emerald-500/5 border-emerald-500/40' :
                      isExpanded ? 'bg-[#0A0A0A] border-[#FF5E1A]/30' : 'bg-transparent border-transparent hover:bg-[#0A0A0A]/50'
                    }`}
                    onClick={() => setExpanded(isExpanded ? null : i)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {v.is_production && (
                          <Badge
                            data-testid={`production-badge-${i}`}
                            className="bg-emerald-500/15 text-emerald-300 text-[8px] px-1.5 py-0 rounded-sm border border-emerald-400/40 shadow-[0_0_8px_rgba(16,185,129,0.4)]"
                          >
                            <Pin className="w-2.5 h-2.5 mr-0.5 inline" />PRODUCTION
                          </Badge>
                        )}
                        {v.is_official && <Badge className="bg-[#FF5E1A]/10 text-[#FF5E1A] text-[8px] px-1 py-0 rounded-sm border border-[#FF5E1A]/20">OFFICIAL</Badge>}
                        {v.checksum_status === 'cal_block_sum_preserved' && (
                          <Badge
                            data-testid={`checksum-ok-${i}`}
                            className="bg-emerald-500/15 text-emerald-300 text-[8px] px-1 py-0 rounded-sm border border-emerald-400/40"
                            title="Cal-block edited but 16-bit BE word sum preserved — safe for sum-based Bosch MSV80 cal CRC"
                          >
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5 inline" />CHECKSUM REPAIRED
                          </Badge>
                        )}
                        {v.needs_external_checksum_fix && (
                          <Badge
                            data-testid={`checksum-warning-${i}`}
                            className="bg-red-500/15 text-red-300 text-[8px] px-1 py-0 rounded-sm border border-red-400/40 shadow-[0_0_8px_rgba(239,68,68,0.3)]"
                            title={`${v.cal_bytes_changed || 0} cal bytes changed — run through WinOLS / MHD Flasher / BimmerLabs checksum tool before flashing`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5 inline" />NEEDS CHECKSUM FIX
                          </Badge>
                        )}
                        {v.is_example && (
                          <Badge data-testid={`example-badge-${i}`} className="bg-amber-500/15 text-amber-300 text-[8px] px-1 py-0 rounded-sm border border-amber-400/40">
                            <Star className="w-2.5 h-2.5 mr-0.5 inline" />PUBLIC EXAMPLE
                          </Badge>
                        )}
                        {v.hidden && (
                          <Badge data-testid={`hidden-badge-${i}`} className="bg-zinc-700/40 text-zinc-400 text-[8px] px-1 py-0 rounded-sm border border-zinc-600/40">
                            <EyeOff className="w-2.5 h-2.5 mr-0.5 inline" />HIDDEN
                          </Badge>
                        )}
                        {v.version && <Badge className="bg-zinc-800 text-zinc-300 text-[9px] px-1.5 py-0 rounded-sm">{v.version}</Badge>}
                        <span className="text-xs font-bold text-white font-['JetBrains_Mono'] truncate">{v.filename}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Admin-only inline curation toggles */}
                        {isAdmin && (
                          <>
                            <button
                              type="button"
                              title={v.is_example ? 'Public example — click to hide from anonymous users' : 'Mark as public example (visible to anonymous users)'}
                              onClick={(e) => { e.stopPropagation(); toggleTuneVisibility(v.filename, { is_example: !v.is_example }); }}
                              className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${v.is_example ? 'text-amber-300' : 'text-zinc-600 hover:text-amber-400'}`}
                              data-testid={`toggle-example-${i}`}
                            >
                              <Star className="w-3.5 h-3.5" fill={v.is_example ? 'currentColor' : 'none'} />
                            </button>
                            <button
                              type="button"
                              title={v.hidden ? 'Hidden — click to unhide' : 'Hide from your timeline'}
                              onClick={(e) => { e.stopPropagation(); toggleTuneVisibility(v.filename, { hidden: !v.hidden }); }}
                              className={`p-1 rounded-sm hover:bg-[#1a1a1a] ${v.hidden ? 'text-zinc-500' : 'text-zinc-600 hover:text-zinc-300'}`}
                              data-testid={`toggle-hidden-${i}`}
                            >
                              {v.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </>
                        )}
                        <span className="text-[10px] text-zinc-600 font-['JetBrains_Mono']">
                          {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}
                        </span>
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                      </div>
                    </div>

                    {v.notes && <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{v.notes}</p>}

                    {isExpanded && (
                      <div className="mt-3 space-y-3">
                        {/* Scaling parameters */}
                        {v.scaling && (
                          <div className="flex flex-wrap gap-1.5">
                            <ScalingBadge label="Idle" value={v.scaling.idle} />
                            <ScalingBadge label="Low" value={v.scaling.low} />
                            <ScalingBadge label="Mid" value={v.scaling.mid} />
                            <ScalingBadge label="High" value={v.scaling.high} />
                            <ScalingBadge label="Load" value={v.scaling.load} />
                            <ScalingBadge label="Fuel" value={v.scaling.combined_fuel_scale} />
                          </div>
                        )}

                        {/* Mods applied (derived tunes) */}
                        {Array.isArray(v.mods_applied) && v.mods_applied.length > 0 && (
                          <div className="text-[10px] text-zinc-400 space-y-0.5">
                            <p className="text-zinc-500 font-['JetBrains_Mono'] uppercase tracking-wider text-[9px]">Mods applied</p>
                            {v.mods_applied.map((m, mi) => (
                              <p key={mi} className="font-['JetBrains_Mono']">
                                • {m.label || m.id}: {Object.entries(m.params || {}).map(([k, val]) => `${k}=${val}`).join(', ')} → ×{typeof m.factor === 'number' ? m.factor.toFixed(4) : m.factor}
                              </p>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="sm"
                            className="border-[#FF5E1A]/30 text-[#FF5E1A] text-[10px] rounded-sm h-7"
                            onClick={(e) => { e.stopPropagation(); window.open(`${API_URL}/api/bin/download/${v.filename}`, '_blank'); }}>
                            <Download className="w-3 h-3 mr-1" />Download
                          </Button>
                          {prevVersion && (
                            <Button variant="outline" size="sm"
                              className="border-cyan-400/30 text-cyan-400 text-[10px] rounded-sm h-7"
                              disabled={diffLoading === diffKey}
                              onClick={(e) => { e.stopPropagation(); loadDiff(prevVersion.filename, v.filename); }}>
                              {diffLoading === diffKey
                                ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                : <GitCompare className="w-3 h-3 mr-1" />
                              }
                              Diff vs {prevVersion.version || 'prev'}
                            </Button>
                          )}
                          {v.is_production ? (
                            <Button variant="outline" size="sm"
                              data-testid={`unpin-btn-${i}`}
                              className="border-emerald-400/40 text-emerald-300 text-[10px] rounded-sm h-7"
                              disabled={pinning === 'unpin'}
                              onClick={(e) => { e.stopPropagation(); unpinTune(); }}>
                              {pinning === 'unpin' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PinOff className="w-3 h-3 mr-1" />}
                              Unpin Production
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm"
                              data-testid={`pin-btn-${i}`}
                              className="border-emerald-500/30 text-emerald-400 text-[10px] rounded-sm h-7 hover:bg-emerald-500/10"
                              disabled={pinning === v.filename}
                              onClick={(e) => { e.stopPropagation(); pinTune(v.filename); }}>
                              {pinning === v.filename ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Pin className="w-3 h-3 mr-1" />}
                              Pin as Production
                            </Button>
                          )}
                        </div>

                        {/* Diff view */}
                        {diffKey && diffData[diffKey] && <DiffView diff={diffData[diffKey]} />}

                        {/* V10-specific prediction chart */}
                        {v.version === 'V10' && <V10PredictionChart />}

                        {/* Prediction Accuracy Scorecard — V9/V10/V11 */}
                        {['V9', 'V10', 'V11', 'V12_SAFE', 'V13_ALPHAN'].includes(v.version) && (
                          <TuneScorecard
                            version={v.version}
                            onGenerateFromInsight={(prefill) => {
                              setDerivePrefill(prefill);
                              setDeriveOpen(true);
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
}
